import { useState } from 'react'
import { addTodo, deleteTodo, toggleTodo } from '../../budget/repo'
import { dateKey } from '../../nutrition/nutrition'
import { useStore } from '../../store'
import { Card, Sheet, Switch, TopBar } from '../../ui/parts'
import { KEEP_COMPLETED_HOURS, groupByDay, isDone, visibleTodos, type Todo } from '../todo'

export function TodoScreen() {
  const { snapshot } = useStore()
  const [adding, setAdding] = useState(false)

  if (!snapshot) return null

  const groups = groupByDay(visibleTodos(snapshot.todos))

  return (
    <div className="screen">
      <TopBar title="To-do" subtitle="Ticked items clear themselves after a day" />

      <div className="stack">
        {groups.length === 0 ? (
          <Card>
            <div className="muted" style={{ fontSize: 14, textAlign: 'center', padding: '14px 0' }}>
              Nothing scheduled.
            </div>
          </Card>
        ) : null}

        {groups.map((group) => (
          <div key={group.date}>
            <div className="month-head">
              <div className="m">{group.label}</div>
            </div>
            <Card tight>
              {group.items.map((todo) => (
                <TodoRow key={todo.id} todo={todo} />
              ))}
            </Card>
          </div>
        ))}
      </div>

      <button type="button" className="fab" onClick={() => setAdding(true)} aria-label="Add task">
        +
      </button>

      {adding ? <TodoSheet onClose={() => setAdding(false)} /> : null}
    </div>
  )
}

function TodoRow({ todo }: { todo: Todo }) {
  const { mutate } = useStore()
  // A daily task is done only for today: ticking it records the date rather than clearing
  // the task, so it is back tomorrow morning.
  const done = isDone(todo)

  return (
    <div className="todo-row">
      <button
        type="button"
        className={`checkbox ${done ? 'on' : ''}`}
        aria-label={done ? 'Mark not done' : 'Mark done'}
        onClick={() => void mutate((s) => toggleTodo(s, todo.id))}
      >
        {done ? '✓' : ''}
      </button>

      <div className="grow">
        <div className={`todo-title ${done ? 'done' : ''}`}>
          {todo.time ? <span className="tnum todo-time">{todo.time}</span> : null}
          {todo.title}
          {todo.repeatDaily ? <span className="chip" style={{ marginLeft: 6 }}>Daily</span> : null}
        </div>
        {todo.description ? (
          <div className="muted" style={{ fontSize: 12 }}>
            {todo.description}
          </div>
        ) : null}
        {done ? (
          <div className="muted" style={{ fontSize: 11 }}>
            {todo.repeatDaily
              ? 'Back again tomorrow'
              : `Clears itself within ${KEEP_COMPLETED_HOURS} hours`}
          </div>
        ) : null}
      </div>

      <button
        type="button"
        className="icon-btn"
        aria-label="Delete task"
        onClick={() => void mutate((s) => deleteTodo(s, todo.id))}
      >
        ✕
      </button>
    </div>
  )
}

function TodoSheet({ onClose }: { onClose: () => void }) {
  const { mutate, showToast } = useStore()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [date, setDate] = useState(dateKey())
  const [time, setTime] = useState('')
  const [repeatDaily, setRepeatDaily] = useState(false)

  return (
    <Sheet onClose={onClose}>
      <h2 style={{ margin: '0 0 14px', fontSize: 20 }}>Add a task</h2>

      <label className="field">
        <span>Title</span>
        <input type="text" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>

      <label className="field">
        <span>Description</span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>

      <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
        {/* A daily task has no date of its own — it is always today's. */}
        {repeatDaily ? null : (
          <label className="field grow" style={{ marginBottom: 0 }}>
            <span>Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
        )}
        <label className="field grow" style={{ marginBottom: 0 }}>
          <span>Time (optional)</span>
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </label>
      </div>

      <div className="row between" style={{ marginTop: 14 }}>
        <div className="grow">
          <div style={{ fontWeight: 550 }}>Repeat every day</div>
          <div className="muted" style={{ fontSize: 12 }}>
            Stays on the list and comes back tomorrow once ticked.
          </div>
        </div>
        <Switch label="Repeat every day" checked={repeatDaily} onChange={setRepeatDaily} />
      </div>

      <div className="row" style={{ marginTop: 18 }}>
        <button
          type="button"
          className="btn grow"
          disabled={title.trim() === '' || (date === '' && !repeatDaily)}
          onClick={async () => {
            const saved = await mutate((s) =>
              addTodo(s, { title, description, date, time: time || null, repeatDaily })
            )
            if (saved) {
              onClose()
              showToast(`Added "${title.trim()}"`)
            }
          }}
        >
          Add task
        </button>
        <button type="button" className="btn ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  )
}
